import { forwardRef, FunctionComponent, PropsWithoutRef, Ref, MouseEvent } from 'react';

import { useI18n } from '../../hooks';
import { Action, ForwardProps, NoChildrenProp } from '../../types';
import Button from '../Button';
import Icon from '../Icon';
import MenuButton from '../MenuButton';

export interface ActionsProps extends NoChildrenProp {
  items: Action[];
  /** @default 2 */
  menuAt?: number;
  /** @default true */
  iconOnly?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

const Actions: FunctionComponent<ActionsProps & ForwardProps> = forwardRef(
  (
    { items, menuAt = 2, iconOnly = true }: PropsWithoutRef<ActionsProps>,
    ref: ActionsProps['ref']
  ) => {
    const t = useI18n();

    if (!items || items.length === 0) {
      return null;
    }

    return items.length >= menuAt ? (
      <MenuButton
        ref={ref}
        text={t('actions')}
        iconOnly
        icon='more'
        variant='simple'
        menu={{
          items: items.map(({ text, ...restProps }) => ({
            primary: text,
            ...restProps
          }))
        }}
      />
    ) : (
      <>
        {items.map(({ id, icon, text, onClick, ...restProps }) => {
          return (
            <Button
              key={id}
              ref={ref}
              variant={icon && iconOnly ? 'simple' : undefined}
              onClick={(event: MouseEvent<HTMLButtonElement>) => onClick?.(id, event)}
              label={icon ? text : undefined}
              icon={!!icon && iconOnly}
              {...restProps}
            >
              {icon && iconOnly ? <Icon name={icon} /> : text}
            </Button>
          );
        })}
      </>
    );
  }
);

export default Actions;
